import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Skeleton,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import PropTypes from 'prop-types';

const DataTable = ({
  columns = [],
  data = [],
  loading = false,
  showSerialNumber = true,
  showActions = false,
  actionsColumnLabel = 'Actions',
  renderActions = null,
  sortConfig = null,
  onSort = null,
  renderRow = null,
  renderCell = null,
  emptyMessage = 'No data found.',
  emptySubMessage = '',
  loadingRows = 5,
  minWidth = { xs: 600, sm: 800, md: 900 },
  sx = {},
  containerSx = {},
}) => {
  const headerCellStyles = {
    fontFamily: 'IBM Plex Sans, sans-serif',
    fontWeight: 600,
    fontStyle: 'normal',
    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
    lineHeight: '20.86px',
    verticalAlign: 'middle',
    px: { xs: '0.75rem', sm: '1rem' },
    py: { xs: '0.5rem', sm: '0.75rem' },
    color: '#ffffff',
    borderBottom: 'none',
  };

  const bodyCellStyles = {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
    lineHeight: '100%',
    verticalAlign: 'middle',
    color: '#525252',
    px: { xs: '0.75rem', sm: '1rem' },
    py: { xs: '0.35rem', sm: '0.4rem' },
  };

  const serialNumberCellStyles = {
    fontFamily: '"JetBrains Mono", monospace',
    fontWeight: 700,
    fontStyle: 'normal',
    fontSize: '0.8125rem',
    lineHeight: '20.86px',
    verticalAlign: 'middle',
    color: '#525252',
    px: { xs: '0.75rem', sm: '1rem' },
    py: { xs: '0.35rem', sm: '0.4rem' },
  };

  const handleSortClick = (columnKey) => {
    if (onSort && columnKey) {
      onSort(columnKey);
    }
  };

  const getSortIcon = (columnKey) => {
    if (!sortConfig || !onSort || !columnKey) return null;

    if (sortConfig.column === columnKey) {
      if (sortConfig.direction === 'asc') {
        return <ArrowUpwardIcon sx={{ fontSize: '0.875rem', ml: 0.5 }} />;
      }
      if (sortConfig.direction === 'desc') {
        return <ArrowDownwardIcon sx={{ fontSize: '0.875rem', ml: 0.5 }} />;
      }
    }
    return (
      <UnfoldMoreIcon
        sx={{
          fontSize: '0.875rem',
          ml: 0.5,
          opacity: 0.5,
        }}
      />
    );
  };

  const totalColumns = columns.length + (showSerialNumber ? 1 : 0) + (showActions ? 1 : 0);

  return (
    <TableContainer
      sx={{
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
          height: '2px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#d0d0d0',
          borderRadius: '1px',
          '&:hover': {
            background: '#b0b0b0',
          },
        },
        ...containerSx,
      }}
    >
      <Table
        size="small"
        sx={{
          minWidth,
          '& .MuiTableCell-root': {
            borderBottom: '1px solid #E4E4E44D',
          },
          ...sx,
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              bgcolor: '#1b2640',
              '&:hover': {
                bgcolor: '#1b2640',
                opacity: 0.95,
              },
              transition: 'background-color 0.2s ease',
            }}
          >
            {showSerialNumber && (
              <TableCell
                sx={{
                  ...headerCellStyles,
                  width: '80px',
                }}
              >
                Sr No
              </TableCell>
            )}

            {columns.map((column) => {
              const isSortable = onSort && column.sortable !== false && column.key;
              return (
                <TableCell
                  key={column.key || column.label}
                  onClick={() => isSortable && handleSortClick(column.key)}
                  sx={{
                    ...headerCellStyles,
                    cursor: isSortable ? 'pointer' : 'default',
                    userSelect: isSortable ? 'none' : 'auto',
                    '&:hover': isSortable
                      ? {
                          bgcolor: 'rgba(255,255,255,0.1)',
                        }
                      : {},
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    {column.label}
                    {isSortable && getSortIcon(column.key)}
                  </Box>
                </TableCell>
              );
            })}

            {showActions && (
              <TableCell
                sx={{
                  ...headerCellStyles,
                }}
              >
                {actionsColumnLabel}
              </TableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            [...Array(loadingRows)].map((_, index) => (
              <TableRow key={index}>
                {showSerialNumber && (
                  <TableCell>
                    <Skeleton variant="rectangular" width={20} height={20} />
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell key={column.key || column.label}>
                    <Skeleton variant="text" width={column.skeletonWidth || 80} />
                  </TableCell>
                ))}
                {showActions && (
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Skeleton variant="circular" width={32} height={32} />
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={totalColumns} align="center" sx={{ py: 4 }}>
                <Typography
                  sx={{
                    color: '#222222',
                    fontSize: '0.95rem',
                    mb: emptySubMessage ? '0.5rem' : 0,
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  }}
                >
                  {emptyMessage}
                </Typography>
                {emptySubMessage && (
                  <Typography
                    sx={{
                      color: '#222222',
                      fontSize: '0.85rem',
                      fontFamily:
                        'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    }}
                  >
                    {emptySubMessage}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ) : (
            data.map((row, index) => {
              if (renderRow) {
                return renderRow(row, index);
              }

              return (
                <TableRow
                  key={row.id || index}
                  sx={{
                    borderBottom: '1px solid #E4E4E44D',
                    backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F9F9F9',
                    cursor: 'pointer',
                    transition: 'background-color 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: '#ECFDF5',
                    },
                  }}
                >
                  {showSerialNumber && (
                    <TableCell sx={serialNumberCellStyles}>{index + 1}</TableCell>
                  )}

                  {columns.map((column) => {
                    const cellValue = row[column.key] || column.defaultValue || '';
                    const cellContent = renderCell
                      ? renderCell(column, row, index)
                      : column.render
                      ? column.render(row, index)
                      : cellValue;

                    return (
                      <TableCell
                        key={column.key || column.label}
                        sx={{
                          ...bodyCellStyles,
                          ...(column.cellSx || {}),
                        }}
                      >
                        {cellContent}
                      </TableCell>
                    );
                  })}

                  {showActions && renderActions && (
                    <TableCell
                      sx={{
                        px: { xs: '0.75rem', sm: '1rem' },
                        py: { xs: '0.35rem', sm: '0.4rem' },
                        verticalAlign: 'middle',
                      }}
                    >
                      {renderActions(row, index)}
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      render: PropTypes.func,
      cellSx: PropTypes.object,
      skeletonWidth: PropTypes.number,
      defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  showSerialNumber: PropTypes.bool,
  showActions: PropTypes.bool,
  actionsColumnLabel: PropTypes.string,
  renderActions: PropTypes.func,
  sortConfig: PropTypes.shape({
    column: PropTypes.string,
    direction: PropTypes.oneOf(['asc', 'desc', 'none']),
  }),
  onSort: PropTypes.func,
  renderRow: PropTypes.func,
  renderCell: PropTypes.func,
  emptyMessage: PropTypes.string,
  emptySubMessage: PropTypes.string,
  loadingRows: PropTypes.number,
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  sx: PropTypes.object,
  containerSx: PropTypes.object,
};

export default DataTable;

